import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  Box,
  Typography,
  Drawer,
  IconButton,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  LinearProgress,
  Tooltip,
  Divider,
  Stack,
  Paper,
  alpha,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import AddIcon from "@mui/icons-material/Add";
import { commonRunQuery } from "../../util/API/loans";
import { PortfolioAdminBulkUpload } from "../../util/API/portfolio";
import { Snackbar, Alert } from "@mui/material";
const DOCUMENT_TYPES = ["User", "Fund"];

const formatBytes = (bytes) => {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};
const SUBFOLDER_TYPES = ["Subscription Agreement", "Tax Docs"];
const getFileColor = (filename) => {
  const ext = filename.split(".").pop().toLowerCase();
  const colors = {
    pdf: "#e53935",
    doc: "#1565c0",
    docx: "#1565c0",
    xls: "#2e7d32",
    xlsx: "#2e7d32",
    ppt: "#e65100",
    pptx: "#e65100",
    jpg: "#6a1b9a",
    jpeg: "#6a1b9a",
    png: "#6a1b9a",
  };
  return colors[ext] || "#546e7a";
};

// ─── File Row Component ───────────────────────────────────────────────────────
const FileRow = ({
  file,
  index,
  onUpdate,
  onRemove,
  entityOptions,
  entityLoading,
  fundOptions,
  fundLoading,
  userRole,
}) => {
  const fileColor = getFileColor(file.name);
  const ext = file.name.split(".").pop().toUpperCase();

  return (
    <TableRow
      sx={{
        "&:hover": { bgcolor: "#f8fafc" },
        transition: "background 0.15s",
        ...(file.status === "error" && { bgcolor: "#fff5f5" }),
        ...(file.status === "done" && { bgcolor: "#f6fff8" }),
      }}
    >
      {/* File Name */}
      <TableCell sx={{ py: 1.5, minWidth: 220 }}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1,
              bgcolor: alpha(fileColor, 0.1),
              border: `1px solid ${alpha(fileColor, 0.25)}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Typography
              sx={{
                fontSize: "0.55rem",
                fontWeight: 800,
                color: fileColor,
                letterSpacing: 0.3,
              }}
            >
              {ext}
            </Typography>
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Tooltip title={file.name} placement="top">
              <Typography
                variant="body2"
                fontWeight={500}
                noWrap
                sx={{ maxWidth: 180, fontSize: "0.8rem", color: "#1a2332" }}
              >
                {file.name}
              </Typography>
            </Tooltip>
            <Typography variant="caption" color="text.secondary">
              {formatBytes(file.size)}
            </Typography>
          </Box>
        </Box>
      </TableCell>

      {/* Document Type Dropdown */}
      <TableCell sx={{ py: 1.5, minWidth: 190 }}>
        <FormControl fullWidth size="small">
          <Select
            displayEmpty
            value={file.docType}
            onChange={(e) => onUpdate(index, "docType", e.target.value)}
            renderValue={(val) =>
              val ? (
                <Typography variant="body2" fontSize="0.8rem">
                  {val}
                </Typography>
              ) : (
                <Typography
                  variant="body2"
                  fontSize="0.8rem"
                  color="text.secondary"
                >
                  Select type...
                </Typography>
              )
            }
            sx={{
              fontSize: "0.8rem",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor:
                  !file.docType && file.touched ? "#e53935" : "#e0e7ef",
              },
              bgcolor: "#fff",
            }}
          >
            {DOCUMENT_TYPES.map((t) => (
              <MenuItem key={t} value={t} sx={{ fontSize: "0.82rem" }}>
                {t}
              </MenuItem>
            ))}
          </Select>
          {!file.docType && file.touched && (
            <Typography
              variant="caption"
              color="error"
              sx={{ mt: 0.25, ml: 0.5 }}
            >
              Required
            </Typography>
          )}
        </FormControl>
      </TableCell>

      {/* Entity Dropdown */}
      <TableCell sx={{ py: 1.5, minWidth: 200 }}>
        <FormControl fullWidth size="small">
          <Select
            displayEmpty
            value={file.entityId}
            onChange={(e) => onUpdate(index, "entityId", e.target.value)}
            disabled={entityLoading}
            renderValue={(val) => {
              if (entityLoading) {
                return (
                  <Box display="flex" alignItems="center" gap={1}>
                    <CircularProgress size={12} />
                    <Typography
                      variant="body2"
                      fontSize="0.8rem"
                      color="text.secondary"
                    >
                      Loading...
                    </Typography>
                  </Box>
                );
              }
              const found = entityOptions.find((e) => e.id === val);
              return found ? (
                <Typography variant="body2" fontSize="0.8rem">
                  {found.name}
                </Typography>
              ) : (
                <Typography
                  variant="body2"
                  fontSize="0.8rem"
                  color="text.secondary"
                >
                  Select entity...
                </Typography>
              );
            }}
            sx={{
              fontSize: "0.8rem",
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e0e7ef" },
              bgcolor: "#fff",
            }}
          >
            {entityOptions.map((e) => (
              <MenuItem key={e.id} value={e.id} sx={{ fontSize: "0.82rem" }}>
                <Typography variant="body2" fontSize="0.82rem">
                  {e.name}
                </Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </TableCell>
      {/* Fund Dropdown — only visible when docType is User */}
      <TableCell sx={{ py: 1.5, minWidth: 190 }}>
        {file.docType === "User" ? (
          <FormControl fullWidth size="small">
            <Select
              displayEmpty
              value={file.userFundId || ""}
              onChange={(e) => onUpdate(index, "userFundId", e.target.value)}
              disabled={
                fundLoading || !file.entityId || file.entityId === "self"
              }
              renderValue={(val) => {
                if (fundLoading)
                  return (
                    <Box display="flex" alignItems="center" gap={1}>
                      <CircularProgress size={12} />
                      <Typography
                        variant="body2"
                        fontSize="0.8rem"
                        color="text.secondary"
                      >
                        Loading...
                      </Typography>
                    </Box>
                  );
                const found = fundOptions.find((f) => f.id === val);
                return found ? (
                  <Typography variant="body2" fontSize="0.8rem">
                    {found.name}
                  </Typography>
                ) : (
                  <Typography
                    variant="body2"
                    fontSize="0.8rem"
                    color="text.secondary"
                  >
                    Select fund...
                  </Typography>
                );
              }}
              sx={{
                fontSize: "0.8rem",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#e0e7ef",
                },
                bgcolor: "#fff",
              }}
            >
              {fundOptions.length === 0 ? (
                <MenuItem
                  disabled
                  sx={{ fontSize: "0.82rem", color: "#b0bec5" }}
                >
                  {file.entityId === "self" || !file.entityId
                    ? "Select a user first"
                    : "No funds found"}
                </MenuItem>
              ) : (
                fundOptions.map((f) => (
                  <MenuItem
                    key={f.id}
                    value={f.id}
                    sx={{ fontSize: "0.82rem" }}
                  >
                    <Typography variant="body2" fontSize="0.82rem">
                      {f.name}
                    </Typography>
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        ) : (
          <Typography variant="caption" color="text.disabled" sx={{ pl: 1 }}>
            —
          </Typography>
        )}
      </TableCell>
      <TableCell sx={{ py: 1.5, minWidth: 150 }}>
        <FormControl fullWidth size="small">
          <Select
            value={file.accessRole}
            onChange={(e) => onUpdate(index, "accessRole", e.target.value)}
            sx={{
              fontSize: "0.8rem",
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e0e7ef" },
              bgcolor: "#fff",
            }}
          >
            <MenuItem value="public" sx={{ fontSize: "0.82rem" }}>
              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: "#2e7d32",
                  }}
                />
                Public
              </Box>
            </MenuItem>
            <MenuItem value="private" sx={{ fontSize: "0.82rem" }}>
              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: "#e53935",
                  }}
                />
                Private
              </Box>
            </MenuItem>
          </Select>
        </FormControl>
      </TableCell>
      {/* Subfolder Dropdown */}
      <TableCell sx={{ py: 1.5, minWidth: 190 }}>
        <FormControl fullWidth size="small">
          <Select
            displayEmpty
            value={file.subFolder}
            onChange={(e) => onUpdate(index, "subFolder", e.target.value)}
            renderValue={(val) =>
              val ? (
                <Typography variant="body2" fontSize="0.8rem">
                  {val}
                </Typography>
              ) : (
                <Typography
                  variant="body2"
                  fontSize="0.8rem"
                  color="text.secondary"
                >
                  Select subfolder...
                </Typography>
              )
            }
            sx={{
              fontSize: "0.8rem",
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e0e7ef" },
              bgcolor: "#fff",
            }}
          >
            {SUBFOLDER_TYPES.map((t) => (
              <MenuItem key={t} value={t} sx={{ fontSize: "0.82rem" }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      bgcolor:
                        t === "SubscriptionAgreement" ? "#1565c0" : "#e65100",
                    }}
                  />
                  {t}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </TableCell>
      {/* Status */}
      <TableCell sx={{ py: 1.5, width: 80 }} align="center">
        {file.status === "done" && (
          <Tooltip title="Uploaded">
            <CheckCircleIcon sx={{ color: "#2e7d32", fontSize: 20 }} />
          </Tooltip>
        )}
        {file.status === "error" && (
          <Tooltip title="Upload failed">
            <ErrorIcon sx={{ color: "#e53935", fontSize: 20 }} />
          </Tooltip>
        )}
        {file.status === "uploading" && (
          <Box sx={{ width: 40 }}>
            <LinearProgress sx={{ borderRadius: 2 }} />
          </Box>
        )}
        {(!file.status || file.status === "pending") && (
          <Typography variant="caption" color="text.disabled">
            —
          </Typography>
        )}
      </TableCell>

      {/* Remove */}
      <TableCell sx={{ py: 1.5, width: 52 }} align="center">
        <Tooltip title="Remove">
          <IconButton
            size="small"
            onClick={() => onRemove(index)}
            disabled={file.status === "uploading" || file.status === "done"}
            sx={{
              color: "#b0bec5",
              "&:hover": { color: "#e53935", bgcolor: "#fff5f5" },
            }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
};

// ─── Drop Zone ────────────────────────────────────────────────────────────────
const DropZone = ({ onFilesAdded }) => {
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef(null);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setIsDragging(false);
      const dropped = Array.from(e.dataTransfer.files);
      if (dropped.length) onFilesAdded(dropped);
    },
    [onFilesAdded],
  );

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = () => setIsDragging(false);

  const handleInputChange = (e) => {
    const selected = Array.from(e.target.files);
    if (selected.length) onFilesAdded(selected);
    e.target.value = "";
  };

  return (
    <Box
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onClick={() => inputRef.current?.click()}
      sx={{
        border: `2px dashed ${isDragging ? "#1976d2" : "#c8d6e5"}`,
        borderRadius: 2,
        p: 3,
        textAlign: "center",
        cursor: "pointer",
        bgcolor: isDragging ? alpha("#1976d2", 0.04) : "#f8fafc",
        transition: "all 0.2s ease",
        "&:hover": { borderColor: "#1976d2", bgcolor: alpha("#1976d2", 0.03) },
      }}
    >
      <input
        ref={inputRef}
        type="file"
        hidden
        multiple
        onChange={handleInputChange}
      />
      <CloudUploadIcon
        sx={{
          fontSize: 36,
          color: isDragging ? "#1976d2" : "#b0bec5",
          mb: 0.5,
          transition: "color 0.2s",
        }}
      />
      <Typography
        variant="body2"
        fontWeight={600}
        color={isDragging ? "#1976d2" : "#546e7a"}
      >
        {isDragging ? "Drop files here" : "Drag & drop files here"}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        or click to browse — PDF, DOC, DOCX, XLS, PPT supported
      </Typography>
    </Box>
  );
};

// ─── Main Drawer Component ────────────────────────────────────────────────────
const BulkUploadDrawer = ({ open, onClose, onUploadComplete, userRole }) => {
  const [files, setFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [entityOptionsMap, setEntityOptionsMap] = useState({});
  const [entityLoadingMap, setEntityLoadingMap] = useState({});
  const [fundOptionsMap, setFundOptionsMap] = useState({});
  const [fundLoadingMap, setFundLoadingMap] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const normalizeToArray = (data) => {
    if (!data) return [];
    return Array.isArray(data) ? data : [data];
  };

  const fetchEntityOptions = useCallback(
    async (docType, index) => {
      const selfOption = [{ id: "self", name: "Self" }];

      if (docType === "User") {
        if (userRole === "admin") {
          setEntityLoadingMap((prev) => ({ ...prev, [index]: true }));
          try {
            const response = await commonRunQuery(364, "");
            console.log("364 RESPONSE======", JSON.stringify(response));
            const records = normalizeToArray(response?.data?.body?.record);

            // const userOptions = records.map((item) => ({
            //   id: item.u.investor_id,
            //   name: `${item.u.firstName} ${item.u.lastName}`,
            // }));

            const userOptions = records.map((item) => {
              const first = item.u.firstName?.trim();
              const last = item.u.lastName?.trim();
            
              const name = (first || last)
                ? `${first || ''} ${last || ''}`.trim()
                : item.u.otherNames;
            
              return {
                id: item.u.investor_id,
                name,
              };
            });

            setEntityOptionsMap((prev) => ({
              ...prev,
              [index]: [{ id: "self", name: "Self" }, ...userOptions],
            }));
          } catch (error) {
            console.error("Error fetching user options:", error);
            setEntityOptionsMap((prev) => ({
              ...prev,
              [index]: [{ id: "self", name: "Self" }],
            }));
          } finally {
            setEntityLoadingMap((prev) => ({ ...prev, [index]: false }));
          }
        } else {
          // investor — only Self
          setEntityOptionsMap((prev) => ({
            ...prev,
            [index]: [{ id: "self", name: "Self" }],
          }));
        }
        return;
      }

      if (docType === "Fund") {
        setEntityLoadingMap((prev) => ({ ...prev, [index]: true }));
        try {
          if (userRole === "admin") {
            const response = await commonRunQuery(359, "");
            const records = normalizeToArray(response?.data?.body?.record);

            const fundOptions = records.map((item) => ({
              id: item.funds.id,
              name: item.funds.name,
            }));

            setEntityOptionsMap((prev) => ({
              ...prev,
              [index]: [...selfOption, ...fundOptions],
            }));
          } else {
            const userId =
              JSON.parse(sessionStorage.getItem("user"))?.userid || "";

            const response = await commonRunQuery(372, userId);

            const records = normalizeToArray(response?.data?.body?.record);

            if (records.length > 0) {
              const fundOptions = records.map((item) => {
                const fund = item.f || item.funds || {};
                return {
                  id: fund.id || fund.fund_id || "—",
                  name: fund.name || fund.fund_name || "—",
                };
              });

              setEntityOptionsMap((prev) => ({
                ...prev,
                [index]: [...selfOption, ...fundOptions],
              }));
            } else {
              setEntityOptionsMap((prev) => ({
                ...prev,
                [index]: selfOption,
              }));
            }
          }
        } catch (error) {
          console.error("Error fetching entity options:", error);
          setEntityOptionsMap((prev) => ({ ...prev, [index]: selfOption }));
        } finally {
          setEntityLoadingMap((prev) => ({ ...prev, [index]: false }));
        }
      }
    },
    [userRole],
  );
  const fetchFundOptions = useCallback(async (ownerUserId, index) => {
    if (!ownerUserId || ownerUserId === "self") {
      setFundOptionsMap((prev) => ({ ...prev, [index]: [] }));
      return;
    }
    setFundLoadingMap((prev) => ({ ...prev, [index]: true }));
    try {
      const response = await commonRunQuery(363, ownerUserId);
      const record = response?.data?.body?.record;
      if (record) {
        const recordsArray = Array.isArray(record) ? record : [record];
        const fundOptions = recordsArray.map((item) => {
          const fund = item.f || item.funds || {};
          return {
            id: fund.id || fund.fund_id || "—",
            name: fund.name || fund.fund_name || "—",
          };
        });
        setFundOptionsMap((prev) => ({ ...prev, [index]: fundOptions }));
      } else {
        setFundOptionsMap((prev) => ({ ...prev, [index]: [] }));
      }
    } catch (err) {
      console.error("Error fetching fund options for user:", err);
      setFundOptionsMap((prev) => ({ ...prev, [index]: [] }));
    } finally {
      setFundLoadingMap((prev) => ({ ...prev, [index]: false }));
    }
  }, []);
  // ── When any file's docType changes, re-fetch entity options ──
  const handleUpdate = (index, field, value) => {
    setFiles((prev) =>
      prev.map((f, i) => {
        if (i !== index) return f;
        const updated = { ...f, [field]: value, touched: true };

        if (field === "docType") {
          updated.entityId = "self";
          updated.userFundId = ""; // ← reset fund selection
          fetchEntityOptions(value, index);
          setFundOptionsMap((prev) => ({ ...prev, [index]: [] })); // ← clear fund options
        }

        if (field === "entityId" && f.docType === "User") {
          updated.userFundId = ""; // ← reset fund on entity change
          fetchFundOptions(value, index); // ← fetch funds for selected user
        }

        return updated;
      }),
    );
  };

  const handleFilesAdded = (newFiles) => {
    const formatted = newFiles.map((f) => ({
      file: f,
      name: f.name,
      size: f.size,
      docType: "",
      entityId: "self", // default to Self
      accessRole: "public",
      subFolder: "",
      userFundId: "",
      status: "pending",
      touched: false,
    }));
    setFiles((prev) => [...prev, ...formatted]);
  };

  const handleRemove = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setEntityOptionsMap((prev) => {
      const n = { ...prev };
      delete n[index];
      return n;
    });
    setEntityLoadingMap((prev) => {
      const n = { ...prev };
      delete n[index];
      return n;
    });
    setFundOptionsMap((prev) => {
      const n = { ...prev };
      delete n[index];
      return n;
    }); // ← add
    setFundLoadingMap((prev) => {
      const n = { ...prev };
      delete n[index];
      return n;
    }); // ← add
  };

  const handleClearAll = () => {
    setFiles([]);
    setEntityOptionsMap({});
    setEntityLoadingMap({});
    setFundOptionsMap({}); // ← add
    setFundLoadingMap({}); // ← add
  };

  const doneCount = files.filter((f) => f.status === "done").length;
  const errorCount = files.filter((f) => f.status === "error").length;
  const pendingCount = files.filter((f) => f.status === "pending").length;
  const allValid = files.length > 0 && files.every((f) => f.docType);

  const handleUploadAll = async () => {
    const anyMissing = files.some((f) => !f.docType);
    if (anyMissing) {
      setFiles((prev) => prev.map((f) => ({ ...f, touched: true })));
      return;
    }

    setIsUploading(true);

    const user = JSON.parse(sessionStorage.getItem("user")).payload?.body;
    const userId = user?.User?.id || "";
    const userPin = "4321";

    const toUpload = files
      .map((f, idx) => ({ ...f, _idx: idx }))
      .filter((f) => f.status !== "done");

    if (toUpload.length === 0) {
      setIsUploading(false);
      return;
    }

    setFiles((prev) =>
      prev.map((f) =>
        f.status !== "done" ? { ...f, status: "uploading" } : f,
      ),
    );

    const documents = toUpload.map((f) => {
      let ownerId = userId;

      if (f.docType === "User") {
        ownerId = f.entityId === "self" ? userId : f.entityId;
      } else if (f.docType === "Fund") {
        ownerId = userId;
      }

      return {
        file: f.file,
        ownerType: f.docType, // per-file docType, not a single userRole
        ownerId: ownerId,
        fundId: f.docType === "Fund" ? f.entityId : f.userFundId || "",
        documentType: f.docType,
        displayName: f.name,
        accessRole: f.accessRole,
        subFolder: f.subFolder || "",
      };
    });

    try {
      const result = await PortfolioAdminBulkUpload(documents, userId, userPin);
      const header = result?.data?.header || result?.header;
      const resultItems = result?.data?.results?.item || result?.results?.item;

      // Normalize to array (single file returns object, multiple returns array)
      const itemsArray = resultItems
        ? Array.isArray(resultItems)
          ? resultItems
          : [resultItems]
        : [];

      // Build a map of filename → status from API response
      const statusByName = {};
      itemsArray.forEach((item) => {
        statusByName[item.file] = item.status === "1" ? "done" : "error";
      });

      setFiles((prev) =>
        prev.map((f, idx) => {
          const wasUploaded = toUpload.some((u) => u._idx === idx);
          if (!wasUploaded) return f;
          // Use per-file status from API if available, fallback to done
          const apiStatus =
            statusByName[f.name] ??
            (header?.callStatus === "1" ? "done" : "error");
          return { ...f, status: apiStatus };
        }),
      );

      // If all succeeded, clear after short delay so user sees the green ticks
      const allSucceeded =
        header?.callStatus === "1" &&
        Object.values(statusByName).every((s) => s === "done");

      if (allSucceeded) {
        setSnackbar({
          open: true,
          message: header?.msg || "Documents uploaded successfully",
          severity: "success",
        });
        setTimeout(() => {
          setFiles([]);
          setEntityOptionsMap({});
          setEntityLoadingMap({});
          if (onUploadComplete) onUploadComplete();
          onClose();
        }, 1500);
      } else {
        setSnackbar({
          open: true,
          message: header?.msg || "Some files failed to upload",
          severity: errorCount > 0 ? "warning" : "success",
        });
        if (onUploadComplete) onUploadComplete();
      }
    } catch (error) {
      console.error("PortfolioAdminBulkUpload failed:", error);
      setFiles((prev) =>
        prev.map((f) =>
          f.status === "uploading" ? { ...f, status: "error" } : f,
        ),
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    if (isUploading) return;
    setFiles([]);
    setEntityOptionsMap({}); // was: setEntityOptions([{ id: "self", name: "Self" }])
    setEntityLoadingMap({});
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: { xs: "100vw", sm: "75vw", lg: "68vw" },
          display: "flex",
          flexDirection: "column",
          bgcolor: "#f4f7fb",
        },
      }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          px: 3,
          py: 2,
          bgcolor: "#002e4c",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <FileUploadIcon sx={{ color: "#fff", fontSize: 22 }} />
          <Box>
            <Typography variant="subtitle1" fontWeight={700} color="#fff">
              Bulk Document Upload
            </Typography>
            {/* {destinationFolder && (
              <Typography variant="caption" sx={{ color: alpha("#fff", 0.6) }}>
                Uploading to:{" "}
                <span style={{ color: "#90caf9", fontWeight: 600 }}>{destinationFolder}</span>
              </Typography>
            )} */}
          </Box>
        </Box>
        <IconButton
          onClick={handleClose}
          disabled={isUploading}
          sx={{ color: "#fff" }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* ── Body ── */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          p: 3,
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
        }}
      >
        <DropZone onFilesAdded={handleFilesAdded} />

        {files.length > 0 && (
          <Box>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={1}
            >
              <Box display="flex" alignItems="center" gap={1.5}>
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  color="#1a2332"
                >
                  {files.length} file{files.length !== 1 ? "s" : ""} queued
                </Typography>
                {doneCount > 0 && (
                  <Chip
                    label={`${doneCount} uploaded`}
                    size="small"
                    color="success"
                    variant="filled"
                    sx={{ fontSize: "0.7rem", height: 20 }}
                  />
                )}
                {errorCount > 0 && (
                  <Chip
                    label={`${errorCount} failed`}
                    size="small"
                    color="error"
                    variant="filled"
                    sx={{ fontSize: "0.7rem", height: 20 }}
                  />
                )}
              </Box>
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  component="label"
                  startIcon={<AddIcon />}
                  sx={{
                    textTransform: "none",
                    fontSize: "0.78rem",
                    color: "#1976d2",
                  }}
                  disabled={isUploading}
                >
                  Add More
                  <input
                    type="file"
                    hidden
                    multiple
                    onChange={(e) =>
                      handleFilesAdded(Array.from(e.target.files))
                    }
                  />
                </Button>
                <Button
                  size="small"
                  onClick={handleClearAll}
                  disabled={isUploading}
                  sx={{
                    textTransform: "none",
                    fontSize: "0.78rem",
                    color: "#78909c",
                  }}
                >
                  Clear All
                </Button>
              </Stack>
            </Box>

            <Paper
              variant="outlined"
              sx={{
                borderRadius: 2,
                overflow: "hidden",
                borderColor: "#e0e7ef",
              }}
            >
              <TableContainer>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow
                      sx={{
                        "& th": {
                          bgcolor: "#f0f4f8",
                          borderBottom: "2px solid #e0e7ef",
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.75rem",
                          color: "#546e7a",
                          py: 1.5,
                        }}
                      >
                        FILE NAME
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.75rem",
                          color: "#546e7a",
                          py: 1.5,
                        }}
                      >
                        DOCUMENT TYPE{" "}
                        <span style={{ color: "#e53935" }}>*</span>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.75rem",
                          color: "#546e7a",
                          py: 1.5,
                        }}
                      >
                        ENTITY <span style={{ color: "#e53935" }}>*</span>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.75rem",
                          color: "#546e7a",
                          py: 1.5,
                        }}
                      >
                        FUND
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.75rem",
                          color: "#546e7a",
                          py: 1.5,
                        }}
                      >
                        ACCESS ROLE <span style={{ color: "#e53935" }}>*</span>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.75rem",
                          color: "#546e7a",
                          py: 1.5,
                        }}
                      >
                        SUBFOLDER
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.75rem",
                          color: "#546e7a",
                          py: 1.5,
                        }}
                        align="center"
                      >
                        STATUS
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {files.map((file, index) => (
                      <FileRow
                        key={`${file.name}-${index}`}
                        file={file}
                        index={index}
                        onUpdate={handleUpdate}
                        onRemove={handleRemove}
                        entityOptions={
                          entityOptionsMap[index] || [
                            { id: "self", name: "Self" },
                          ]
                        } // ← per-row
                        entityLoading={entityLoadingMap[index] || false}
                        fundOptions={fundOptionsMap[index] || []} // ← add
                        fundLoading={fundLoadingMap[index] || false} // ← add // ← per-row
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            {isUploading && (
              <Box mt={1.5}>
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography variant="caption" color="text.secondary">
                    Uploading {doneCount + errorCount} of {files.length}...
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {Math.round(
                      ((doneCount + errorCount) / files.length) * 100,
                    )}
                    %
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={((doneCount + errorCount) / files.length) * 100}
                  sx={{
                    borderRadius: 2,
                    height: 6,
                    bgcolor: "#e0e7ef",
                    "& .MuiLinearProgress-bar": { bgcolor: "#002e4c" },
                  }}
                />
              </Box>
            )}

            {!isUploading && errorCount > 0 && (
              <Box
                mt={1.5}
                p={1.5}
                sx={{
                  bgcolor: "#fff5f5",
                  border: "1px solid #ffcdd2",
                  borderRadius: 1.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <ErrorIcon
                  sx={{ color: "#e53935", fontSize: 16, flexShrink: 0 }}
                />
                <Typography variant="caption" color="#c62828">
                  {errorCount} file{errorCount > 1 ? "s" : ""} failed to upload.
                  Remove them or click <strong>Upload All</strong> again to
                  retry.
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {files.length === 0 && (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 4,
              color: "#b0bec5",
              gap: 1,
            }}
          >
            <InsertDriveFileIcon sx={{ fontSize: 48, opacity: 0.4 }} />
            <Typography variant="body2" color="text.disabled">
              No files added yet. Drag & drop or click above to get started.
            </Typography>
          </Box>
        )}
      </Box>

      {/* ── Footer ── */}
      <Divider />
      <Box
        sx={{
          px: 3,
          py: 2,
          bgcolor: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
          gap: 2,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {files.length === 0
            ? "Add files above to get started"
            : allValid
              ? `${pendingCount + errorCount} file${pendingCount + errorCount !== 1 ? "s" : ""} ready to upload`
              : "Fill in Document Type and Entity for all files before uploading"}
        </Typography>

        <Box display="flex" gap={1.5}>
          <Button
            variant="outlined"
            onClick={handleClose}
            disabled={isUploading}
            sx={{
              textTransform: "none",
              borderColor: "#c8d6e5",
              color: "#546e7a",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUploadAll}
            disabled={
              files.length === 0 || isUploading || doneCount === files.length
            }
            startIcon={<FileUploadIcon />}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              bgcolor: "#002e4c",
              px: 3,
              "&:hover": { bgcolor: "#004a7a" },
              "&.Mui-disabled": { bgcolor: "#b0bec5", color: "#fff" },
            }}
          >
            {isUploading
              ? "Uploading..."
              : doneCount === files.length && files.length > 0
                ? "All Uploaded ✓"
                : `Upload All (${files.filter((f) => f.status !== "done").length})`}
          </Button>
        </Box>
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%", fontWeight: 600 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Drawer>
  );
};

export default BulkUploadDrawer;
